import React from 'react';
import { Link } from 'react-router-dom';
import HealthPill from '../HealthPill';
import { fmtAge, fmtClock, fmtDay, fmtStamp, STATE_GREY_FILL, STATE_GREY_HATCH } from '../lib/chartTheme';
import { serviceSpans, toRows } from '../lib/deliveryChartRows';
import { DAY_MS } from '../lib/recordWindow';
import { Panel } from './primitives';

const STATE_LABEL = {
    measured: 'In service',
    out_of_service: 'Out of service',
    unmeasured: 'Not measured',
};

const STATE_FILL = {
    measured: 'rgba(68,131,97,0.35)',
    out_of_service: STATE_GREY_FILL,
    unmeasured: `repeating-linear-gradient(45deg, ${STATE_GREY_HATCH} 0 2px, transparent 2px 6px)`,
};

/**
 * The battery's operating state as the settled record has it, from the newest row: measured
 * (in service), out of service (every member contractually disabled) or unmeasured (the
 * statistics job saw no member — a monitoring gap, not an operating state).
 *
 * @param {import('../lib/marketReportsV1').Delivered[]} reports oldest first
 */
export const latestState = reports => (reports.length === 0 ? null : reports[reports.length - 1]);

const stamp = iso => (iso ? fmtStamp(Date.parse(iso)) : null);

/**
 * How the battery is operating right now, by three sources that are never merged:
 *
 * - the settled record says whether it was in service (newest delivery row), drawn as a
 *   24-hour strip;
 * - the health reports say whether each member is working — in **three** states, because the
 *   health job only writes while a device is in service and an absent report is not a green;
 * - gateway liveness is per-member enrichment for the members that have a gateway, and
 *   fails open for the ones that do not.
 */
const OperatingStatus = ({ battery, reports, reportsReady, memberStatus, nowMs = Date.now() }) => {
    const latest = latestState(reports);
    const state = latest?.measurement ?? null;
    const windowEnd = nowMs;
    const windowStart = nowMs - DAY_MS;
    const spans = serviceSpans(toRows(reports)).filter(span => span.to > windowStart && span.from < windowEnd);
    const pct = t => (Math.min(windowEnd, Math.max(windowStart, t)) - windowStart) / DAY_MS;

    const sentence = () => {
        if (!reportsReady) {
            return <span className="vb-status__sentence--muted">Loading the settled record…</span>;
        }
        if (latest === null) {
            return <span className="vb-status__sentence--muted">No settled delivery report in the last 7 days.</span>;
        }
        const tone = state === 'measured' ? 'ok' : state === 'out_of_service' ? 'muted' : 'warn';
        return (
            <>
                <span className={`vb-status__sentence--${tone}`}>{STATE_LABEL[state]}</span>
                <span className="vb-status__sentence--muted">
                    {' '}
                    as of {stamp(latest.end)} ({fmtAge(nowMs - Date.parse(latest.end))} ago)
                    {state === 'measured' && latest.disabledDeviceCount > 0
                        ? ` · ${latest.deviceCount} of ${latest.deviceCount + latest.disabledDeviceCount} devices aggregated`
                        : state === 'unmeasured'
                        ? ' · the statistics job saw no device for this period'
                        : ''}
                </span>
            </>
        );
    };

    return (
        <Panel
            icon="fa-heart-pulse"
            title="Operating status"
            info="In service / out of service is the settled record's own reading of each 15-minute period (newest row for the sentence, last 24 h for the strip). Device rows are the latest health report per device; a device with no report is not reporting, not healthy. Gateway status is shown for the devices that have a gateway."
        >
            <p className="vb-status__sentence">{sentence()}</p>

            <div className="vb-strip" role="img" aria-label="Service state over the last 24 hours">
                <div className="vb-strip__bar">
                    {spans.map(span => (
                        <div
                            key={`${span.state}-${span.from}`}
                            className="vb-strip__span"
                            title={`${STATE_LABEL[span.state]} ${fmtClock(Math.max(span.from, windowStart))}–${fmtClock(
                                Math.min(span.to, windowEnd)
                            )} UTC`}
                            style={{
                                left: `${pct(span.from) * 100}%`,
                                width: `${(pct(span.to) - pct(span.from)) * 100}%`,
                                background: STATE_FILL[span.state],
                            }}
                        />
                    ))}
                </div>
                <div className="vb-strip__axis">
                    <span>
                        {fmtDay(windowStart)} {fmtClock(windowStart)}
                    </span>
                    <span>UTC</span>
                    <span>
                        {fmtDay(windowEnd)} {fmtClock(windowEnd)}
                    </span>
                </div>
                <div className="vb-strip__legend">
                    <span>
                        <i className="vb-strip__swatch" style={{ background: STATE_FILL.measured }} /> In service
                    </span>
                    <span>
                        <i className="vb-strip__swatch" style={{ background: STATE_FILL.out_of_service }} /> Out of service
                    </span>
                    <span>
                        <i className="vb-strip__swatch" style={{ background: STATE_FILL.unmeasured }} /> Not measured
                    </span>
                    <span>
                        <i className="vb-strip__swatch" /> No report
                    </span>
                </div>
            </div>

            <table className="vb-members">
                <thead>
                    <tr>
                        <th>Device</th>
                        <th>Health</th>
                        <th>Gateway</th>
                    </tr>
                </thead>
                <tbody>
                    {battery.members.map(member => {
                        const health = memberStatus.healthByDevice.get(member.deviceId);
                        const gateway = memberStatus.gatewayByDevice.get(member.deviceId);
                        return (
                            <tr key={member.deviceId}>
                                <td className="vb-table__mono">
                                    {gateway ? <Link to={`/gateways/${member.deviceId}`}>{member.deviceId}</Link> : member.deviceId}
                                </td>
                                <td>
                                    {!memberStatus.isReady ? (
                                        '…'
                                    ) : health ? (
                                        <HealthPill
                                            status={health.status}
                                            detail={
                                                health.reportedAt
                                                    ? `${stamp(health.reportedAt)}${
                                                          health.alertCount > 0 ? ` · ${health.alertCount} alerts (7 d)` : ''
                                                      }`
                                                    : null
                                            }
                                        />
                                    ) : (
                                        <HealthPill status={null} />
                                    )}
                                </td>
                                <td>
                                    {!memberStatus.isReady ? (
                                        '…'
                                    ) : gateway ? (
                                        <span
                                            className={`gw-pill ${
                                                gateway.status === 'CONNECTED'
                                                    ? 'gw--connected'
                                                    : gateway.status === 'LOST'
                                                    ? 'gw--lost'
                                                    : 'gw--unknown'
                                            }`}
                                        >
                                            <span className="gw-pill__dot" />
                                            {gateway.status === 'CONNECTED' ? 'Connected' : gateway.status === 'LOST' ? 'Lost' : 'Unknown'}
                                            {gateway.lastHeartbeat ? (
                                                <span className="vb-health__detail">· {fmtAge(nowMs - Date.parse(gateway.lastHeartbeat))} ago</span>
                                            ) : null}
                                        </span>
                                    ) : (
                                        <span className="vb-status__sentence--muted">No gateway</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    {battery.members.length === 0 ? (
                        <tr>
                            <td colSpan={3} className="vb-status__sentence--muted">
                                This battery has no devices.
                            </td>
                        </tr>
                    ) : null}
                </tbody>
            </table>
        </Panel>
    );
};

export default OperatingStatus;
